import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Columns3, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Checkbox } from '@components/ui/checkbox'
import { Input } from '@components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu'
import { AppliedFilterPills, FilterField, FiltersPopover } from '@components/settings/table-filters'
import { listAdasCategoryMappings } from '../../../api/scrubApi'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { FormModal, FormModalFooter } from '@components/branded-modal'

const ANY = '__any__'

const MATCH_TYPE_OPTIONS = [
  { value: 'UNIQUE_KEY', label: 'Unique Key' },
  { value: 'FEATURE_NAME', label: 'Feature Name' }
]

// AdasCategory enum name → display label. The mappings API stores/filters by the
// enum NAME (exact match), unlike the rules API which serializes labels — so the
// filter dropdown sends names while the UI shows the friendly label.
const ADAS_CATEGORY_LABELS = {
  FRONT_RADAR: 'Front Radar',
  WINDSHIELD_CAMERA: 'Windshield Camera',
  BLIND_SPOT_MONITORING: 'Blind Spot Monitoring',
  REAR_CAMERA: 'Rear Camera',
  SURROUND_VIEW_CAMERA: 'Surround View Camera',
  PARK_ASSIST: 'Park Assist',
  FRONT_CORNER_RADAR: 'Front Corner Radar',
  OCCUPANT_DETECTION: 'Occupant Detection',
  STEERING_ANGLE_SENSOR: 'Steering Angle Sensor',
  WHEEL_ALIGNMENT: 'Wheel Alignment',
  LANE_KEEP_ASSIST: 'Lane Keep Assist',
  AVM: 'AVM',
  NIGHT_VISION: 'Night Vision',
  SRS_RESTRAINT_SYSTEM: 'SRS / Restraint System',
  NONE: 'None',
  PARK_ASSIST_FRONT: 'Park Assist',
  PARK_ASSIST_REAR: 'Park Assist'
}

const categoryLabel = (name) => ADAS_CATEGORY_LABELS[name] || name || ''
const matchTypeLabel = (value) => MATCH_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || ''

const PAGE_SIZES = [10, 20, 50, 100]

// ── Placeholder APIs ─────────────────────────────────────────────────────────
// The BE exposes PUT /v1/intelligence/adas-category-mappings/{id} and
// DELETE /v1/intelligence/adas-category-mappings/{id} (soft delete), but they are
// not wired yet. These stubs resolve immediately so the UI flow works end-to-end
// against local state; swap the bodies for real scrubApi wrappers to go live.

/** TODO: wire to POST /v1/intelligence/adas-category-mappings. */
const createMappingPlaceholder = async (mapping) => mapping

/** TODO: wire to PUT /v1/intelligence/adas-category-mappings/{id}. */
const updateMappingPlaceholder = async (mapping) => mapping

/** TODO: wire to DELETE /v1/intelligence/adas-category-mappings/{id} per id. */
const deleteMappingsPlaceholder = async (ids) => ids

/** Format an ISO timestamp as "May 4, 2026". */
const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Column registry: key = DTO field (or synthetic), label = header text.
 *  `sortValue` extracts the comparable value; `render` the cell content.
 *  All columns are visible by default; any can be hidden from the Columns dropdown. */
const COLUMNS = [
  {
    key: 'id',
    label: 'ID',
    sortValue: (m) => m.id ?? 0,
    render: (m) => <span className='font-mono text-[#475569]'>{m.id}</span>
  },
  {
    key: 'matchType',
    label: 'Match Type',
    sortValue: (m) => m.matchType || '',
    render: (m) => matchTypeLabel(m.matchType)
  },
  {
    key: 'matchKey',
    label: 'Match Key',
    sortValue: (m) => m.matchKey || '',
    render: (m) => (
      <div className='max-w-[280px] truncate font-mono text-[13px] text-[#0A1628]' title={m.matchKey}>
        {m.matchKey}
      </div>
    )
  },
  {
    key: 'adasCategory',
    label: 'ADAS Category',
    sortValue: (m) => categoryLabel(m.adasCategory),
    render: (m) => categoryLabel(m.adasCategory)
  },
  {
    key: 'appliesToOems',
    label: 'Applies To OEMs',
    sortValue: (m) => (m.appliesToOems?.length ? m.appliesToOems.join(', ') : ''),
    render: (m) =>
      m.appliesToOems?.length ? (
        <div className='max-w-[240px] truncate' title={m.appliesToOems.join(', ')}>
          {m.appliesToOems.join(', ')}
        </div>
      ) : (
        <span className='text-[#6B7280]'>All OEMs</span>
      )
  },
  {
    key: 'isActive',
    label: 'Status',
    sortValue: (m) => (m.isActive ? 1 : 0),
    render: (m) => {
      const color = m.isActive ? '#059669' : '#6B7280'
      return (
        <div className='inline-flex items-center gap-1.5'>
          <span className='inline-block h-2 w-2 rounded-full' style={{ background: color }} />
          <span className='text-[12px] font-medium' style={{ color }}>
            {m.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      )
    }
  },
  {
    key: 'createdAt',
    label: 'Created',
    sortValue: (m) => (m.createdAt ? new Date(m.createdAt).getTime() : 0),
    render: (m) => <span className='whitespace-nowrap text-[13px] text-[#475569]'>{formatDate(m.createdAt)}</span>
  },
  { key: 'createdBy', label: 'Created By', sortValue: (m) => m.createdBy || '' },
  {
    key: 'updatedAt',
    label: 'Updated',
    sortValue: (m) => (m.updatedAt ? new Date(m.updatedAt).getTime() : 0),
    render: (m) => <span className='whitespace-nowrap text-[13px] text-[#475569]'>{formatDate(m.updatedAt)}</span>
  },
  { key: 'updatedBy', label: 'Updated By', sortValue: (m) => m.updatedBy || '' }
]

const allColumnKeys = () => new Set(COLUMNS.map((c) => c.key))

const SortIcon = ({ direction }) => {
  if (direction === 'asc') return <ArrowUp className='h-3.5 w-3.5' />
  if (direction === 'desc') return <ArrowDown className='h-3.5 w-3.5' />
  return <ArrowUpDown className='h-3.5 w-3.5 opacity-50' />
}

/**
 * Settings → Mappings: read-only browser over the ChromeData→AdasCategory mapping
 * table (GET /v1/intelligence/adas-category-mappings). Server-side filtering +
 * pagination; the BE exposes no sort param (results come back ordered by
 * matchType, adasCategory, matchKey), so column sorting is client-side over the
 * currently loaded page.
 */
export const MappingsSettings = () => {
  // Committed (debounced) filter values — these drive the fetch.
  const [filters, setFilters] = useState({ key: '', matchType: '', adasCategory: '', active: 'true' })
  // Raw text-input value, debounced into `filters`.
  const [keyInput, setKeyInput] = useState('')

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [rows, setRows] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const [sort, setSort] = useState({ key: null, direction: null })
  const [visibleKeys, setVisibleKeys] = useState(allColumnKeys)

  // Row selection (quotes-page pattern) + edit/delete dialog state.
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [editRow, setEditRow] = useState(null) // original mapping behind the edit dialog
  const [editDraft, setEditDraft] = useState(null) // editable field values
  const [deleteIds, setDeleteIds] = useState(null) // mapping ids pending delete confirm
  const [isMutating, setIsMutating] = useState(false)

  // Debounce the free-text input into the committed filters (and reset paging).
  useEffect(() => {
    const h = setTimeout(() => {
      setFilters((prev) => {
        if (prev.key === keyInput) return prev
        setPage(0)
        return { ...prev, key: keyInput }
      })
    }, 350)
    return () => clearTimeout(h)
  }, [keyInput])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await listAdasCategoryMappings({
          page,
          size,
          matchType: filters.matchType,
          adasCategory: filters.adasCategory,
          key: filters.key,
          active: filters.active === 'true'
        })
        if (cancelled) return
        setRows(Array.isArray(data?.content) ? data.content : [])
        setTotalElements(data?.totalElements ?? 0)
        setTotalPages(data?.totalPages ?? 0)
      } catch (err) {
        if (!cancelled) setError(getFriendlyErrorMessage(err, 'Failed to load ADAS category mappings'))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [page, size, filters])

  const setSelectFilter = useCallback((key, value) => {
    setPage(0)
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Filters living inside the popover (everything except the inline key search).
  const popoverFilterCount = [filters.matchType, filters.adasCategory, filters.active !== 'true'].filter(Boolean).length

  const hasActiveFilters = filters.key || popoverFilterCount > 0

  const clearFilters = () => {
    setKeyInput('')
    setPage(0)
    setFilters({ key: '', matchType: '', adasCategory: '', active: 'true' })
  }

  // Per-pill clear for the key search: reset the raw input too, so the debounce
  // effect doesn't immediately re-commit the cleared value.
  const clearKeyFilter = () => {
    setKeyInput('')
    setPage(0)
    setFilters((prev) => ({ ...prev, key: '' }))
  }

  const appliedPills = [
    filters.key && { key: 'key', label: 'Match Key', value: filters.key, onClear: clearKeyFilter },
    filters.matchType && {
      key: 'matchType',
      label: 'Match Type',
      value: matchTypeLabel(filters.matchType),
      onClear: () => setSelectFilter('matchType', '')
    },
    filters.adasCategory && {
      key: 'adasCategory',
      label: 'Category',
      value: categoryLabel(filters.adasCategory),
      onClear: () => setSelectFilter('adasCategory', '')
    },
    filters.active !== 'true' && { key: 'active', label: 'Status', value: 'Inactive', onClear: () => setSelectFilter('active', 'true') }
  ].filter(Boolean)

  // Cycle a header through asc → desc → unsorted.
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => visibleKeys.has(c.key)), [visibleKeys])

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows
    const col = COLUMNS.find((c) => c.key === sort.key)
    if (!col) return rows
    const dir = sort.direction === 'desc' ? -1 : 1
    return [...rows].sort((a, b) => {
      const va = col.sortValue(a)
      const vb = col.sortValue(b)
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true }) * dir
    })
  }, [rows, sort])

  const toggleColumn = (key) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        if (next.size === 1) return prev // keep at least one column visible
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // ── Selection / edit / delete ──────────────────────────────────────────────

  const toggleSelect = (mapping) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(mapping.id)) next.delete(mapping.id)
      else next.add(mapping.id)
      return next
    })
  }

  const openEdit = (mapping) => {
    setEditRow(mapping)
    setEditDraft({
      matchType: mapping.matchType || 'UNIQUE_KEY',
      matchKey: mapping.matchKey || '',
      adasCategory: mapping.adasCategory || '',
      // Edited as comma-separated text, parsed back to an array on save.
      appliesToOems: mapping.appliesToOems?.join(', ') || '',
      isActive: mapping.isActive ? 'true' : 'false'
    })
  }

  // Create mode reuses the edit dialog with default fields (editRow stays null).
  const openCreate = () => {
    setEditRow(null)
    setEditDraft({ matchType: 'UNIQUE_KEY', matchKey: '', adasCategory: '', appliesToOems: '', isActive: 'true' })
  }

  const closeEditDialog = () => {
    setEditRow(null)
    setEditDraft(null)
  }

  const saveEdit = async () => {
    setIsMutating(true)
    try {
      const fields = {
        matchType: editDraft.matchType,
        matchKey: editDraft.matchKey.trim(),
        adasCategory: editDraft.adasCategory,
        appliesToOems: editDraft.appliesToOems
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        isActive: editDraft.isActive === 'true'
      }
      if (editRow) {
        const updated = { ...editRow, ...fields }
        await updateMappingPlaceholder(updated)
        setRows((prev) => prev.map((m) => (m.id === editRow.id ? updated : m)))
      } else {
        // Temporary local id until the BE assigns the real one on create.
        const created = { id: Date.now(), ...fields, createdAt: new Date().toISOString() }
        await createMappingPlaceholder(created)
        setRows((prev) => [created, ...prev])
      }
      closeEditDialog()
    } finally {
      setIsMutating(false)
    }
  }

  const confirmDelete = async () => {
    setIsMutating(true)
    try {
      await deleteMappingsPlaceholder(deleteIds)
      setRows((prev) => prev.filter((m) => !deleteIds.includes(m.id)))
      setSelectedIds((prev) => {
        const next = new Set(prev)
        deleteIds.forEach((id) => next.delete(id))
        return next
      })
      setDeleteIds(null)
    } finally {
      setIsMutating(false)
    }
  }

  return (
    <div className='mx-auto max-w-[1400px] mt-9'>
      {/* Page header */}
      <div className='mb-5'>
        <h1 className='text-[32px] font-bold leading-tight text-foreground'>Mappings</h1>
        <p className='mt-1 text-[14px] text-muted-foreground'>
          ADAS category mappings used to match estimate line items to the rule library.
        </p>
      </div>

      {/* Filters toolbar — one search box inline; everything else in the Filters popover */}
      <div className='mb-4 flex flex-wrap items-center gap-3'>
        <Input
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder='Search match key...'
          className='h-9 w-[220px] bg-white'
          aria-label='Search match key'
        />
        <FiltersPopover activeCount={popoverFilterCount}>
          <FilterField label='Match Type'>
            <Select value={filters.matchType || ANY} onValueChange={(v) => setSelectFilter('matchType', v === ANY ? '' : v)}>
              <SelectTrigger className='bg-white' aria-label='Filter by match type'>
                <SelectValue placeholder='All Match Types' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All Match Types</SelectItem>
                {MATCH_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label='ADAS Category'>
            <Select value={filters.adasCategory || ANY} onValueChange={(v) => setSelectFilter('adasCategory', v === ANY ? '' : v)}>
              <SelectTrigger className='bg-white' aria-label='Filter by ADAS category'>
                <SelectValue placeholder='All Categories' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All Categories</SelectItem>
                {Object.entries(ADAS_CATEGORY_LABELS).map(([name, label]) => (
                  <SelectItem key={name} value={name}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label='Status'>
            <Select value={filters.active} onValueChange={(v) => setSelectFilter('active', v)}>
              <SelectTrigger className='bg-white' aria-label='Filter by status'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='true'>Active</SelectItem>
                <SelectItem value='false'>Inactive</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </FiltersPopover>

        {/* Add + column visibility */}
        <div className='ml-auto flex items-center gap-2'>
          <Button size='sm' className='bg-sidebar text-white hover:bg-sidebar/90' onClick={openCreate}>
            <Plus className='mr-1 h-4 w-4' />
            Add Mapping
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm' className='bg-white'>
                <Columns3 className='mr-1.5 h-4 w-4' />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='max-h-[320px] w-[220px] overflow-y-auto'>
              <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={visibleKeys.has(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Applied filters */}
      <AppliedFilterPills pills={appliedPills} onClearAll={clearFilters} />

      {error && <div className='mb-4 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>{error}</div>}

      {/* Selection action bar (quotes-page pattern) */}
      {selectedIds.size > 0 && (
        <div
          className='mb-3 flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 text-white'
          style={{ background: 'var(--primary-gradient)' }}
        >
          <span className='text-[14px] font-semibold'>{selectedIds.size} selected</span>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setDeleteIds(Array.from(selectedIds))}
              className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#B91C1C] transition-colors hover:bg-[#F8FAFC]'
            >
              <Trash2 className='h-3.5 w-3.5' />
              Delete
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className='overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
        <div className='overflow-x-auto'>
          <Table>
            <TableHeader>
              <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
                <TableHead className='w-12 text-white' aria-label='Selection' />
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className='whitespace-nowrap text-white'>
                    <button
                      type='button'
                      onClick={() => toggleSort(col.key)}
                      className='inline-flex items-center gap-1.5 hover:text-white/80'
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      <SortIcon direction={sort.key === col.key ? sort.direction : null} />
                    </button>
                  </TableHead>
                ))}
                {/* Sticky on the right; solid bg (gradients don't inherit) so scrolling columns pass under it */}
                <TableHead className='sticky right-0 z-10 w-20 bg-[#1F2D3D] text-white shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.3)]'>
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} className='py-12 text-center'>
                    <Loader2 className='mx-auto h-5 w-5 animate-spin text-[#1F6FEB]' />
                    <div className='mt-2 text-sm text-[#6B7280]'>Loading mappings...</div>
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} className='py-10 text-center text-sm text-[#6B7280]'>
                    {hasActiveFilters ? 'No mappings match the selected filters.' : 'No ADAS category mappings found.'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((mapping) => {
                  const isSelected = selectedIds.has(mapping.id)
                  return (
                    <TableRow
                      key={mapping.id}
                      data-state={isSelected ? 'selected' : undefined}
                      className='h-12 bg-white even:bg-table-row-even hover:bg-table-row-hover data-[state=selected]:bg-[#E8EFFF] data-[state=selected]:hover:bg-[#E8EFFF]'
                    >
                      <TableCell className='align-middle'>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(mapping)}
                          aria-label={`Select mapping ${mapping.id}`}
                        />
                      </TableCell>
                      {visibleColumns.map((col) => (
                        <TableCell key={col.key} className='align-middle text-[13px] text-[#0A1628]'>
                          {col.render ? col.render(mapping) : col.sortValue(mapping) || <span className='text-[#6B7280]'>—</span>}
                        </TableCell>
                      ))}
                      {/* bg-inherit follows the row's stripe/hover/selected color */}
                      <TableCell className='sticky right-0 z-10 bg-inherit align-middle shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.15)]'>
                        <div className='flex items-center gap-1'>
                          <button
                            type='button'
                            onClick={() => openEdit(mapping)}
                            aria-label={`Edit mapping ${mapping.id}`}
                            className='flex h-7 w-7 items-center justify-center rounded-md text-[#475569] hover:bg-[#EFF6FF] hover:text-[#1F6FEB]'
                          >
                            <Pencil className='h-3.5 w-3.5' />
                          </button>
                          <button
                            type='button'
                            onClick={() => setDeleteIds([mapping.id])}
                            aria-label={`Delete mapping ${mapping.id}`}
                            className='flex h-7 w-7 items-center justify-center rounded-md text-[#475569] hover:bg-[#FEF2F2] hover:text-[#B91C1C]'
                          >
                            <Trash2 className='h-3.5 w-3.5' />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination footer */}
      <div className='mt-3 flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <div className='text-[12px] text-[#6B7280]'>
            {totalElements > 0
              ? `Showing ${page * size + 1}–${Math.min((page + 1) * size, totalElements)} of ${totalElements}`
              : 'No results'}
          </div>
          <Select
            value={String(size)}
            onValueChange={(v) => {
              setPage(0)
              setSize(Number(v))
            }}
          >
            <SelectTrigger className='h-8 w-[130px] bg-white text-[12px]' aria-label='Rows per page'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s} per page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex items-center gap-1'>
          <Button variant='outline' size='sm' disabled={page <= 0 || isLoading} onClick={() => setPage(Math.max(0, page - 1))}>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <span className='px-2 text-[12px] text-[#6B7280]'>
            Page {page + 1} / {Math.max(totalPages, 1)}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages - 1 || isLoading}
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>

      {/* Add / edit dialog — pre-filled from the selected mapping, defaults in create mode */}
      <FormModal
        open={!!editDraft}
        onClose={closeEditDialog}
        title={editRow ? `Edit mapping #${editRow.id}` : 'Add mapping'}
        subtitle={
          editRow
            ? 'Update how this ChromeData key resolves to an ADAS category.'
            : 'Map a ChromeData key or feature name to an ADAS category.'
        }
        width='max-w-[440px]'
        footer={
          <FormModalFooter
            onCancel={closeEditDialog}
            cancelDisabled={isMutating}
            onConfirm={saveEdit}
            confirmDisabled={isMutating || !editDraft?.matchKey?.trim() || !editDraft?.adasCategory}
            confirmLabel={
              <>
                {isMutating && <Loader2 className='h-4 w-4 animate-spin' />}
                {editRow ? 'Save' : 'Add'}
              </>
            }
          />
        }
      >
        {editDraft && (
          <div className='flex flex-col gap-4 py-2'>
            <FilterField label='Match Type'>
              <Select value={editDraft.matchType} onValueChange={(v) => setEditDraft((d) => ({ ...d, matchType: v }))}>
                <SelectTrigger className='bg-white' aria-label='Match type'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATCH_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label='Match Key'>
              <Input
                value={editDraft.matchKey}
                onChange={(e) => setEditDraft((d) => ({ ...d, matchKey: e.target.value }))}
                className='font-mono'
                aria-label='Match key'
              />
            </FilterField>
            <FilterField label='ADAS Category'>
              <Select value={editDraft.adasCategory} onValueChange={(v) => setEditDraft((d) => ({ ...d, adasCategory: v }))}>
                <SelectTrigger className='bg-white' aria-label='ADAS category'>
                  <SelectValue placeholder='Select a category' />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ADAS_CATEGORY_LABELS).map(([name, label]) => (
                    <SelectItem key={name} value={name}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterField label='Applies To OEMs (comma-separated, empty = all OEMs)'>
              <Input
                value={editDraft.appliesToOems}
                onChange={(e) => setEditDraft((d) => ({ ...d, appliesToOems: e.target.value }))}
                placeholder='All OEMs'
                aria-label='Applies to OEMs'
              />
            </FilterField>
            <FilterField label='Status'>
              <Select value={editDraft.isActive} onValueChange={(v) => setEditDraft((d) => ({ ...d, isActive: v }))}>
                <SelectTrigger className='bg-white' aria-label='Status'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='true'>Active</SelectItem>
                  <SelectItem value='false'>Inactive</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>
          </div>
        )}
      </FormModal>

      {/* Delete confirm dialog */}
      <FormModal
        open={!!deleteIds}
        onClose={() => setDeleteIds(null)}
        title={`Delete ${deleteIds?.length > 1 ? `${deleteIds.length} mappings` : 'mapping'}?`}
        width='max-w-[420px]'
        footer={
          <FormModalFooter
            onCancel={() => setDeleteIds(null)}
            cancelDisabled={isMutating}
            onConfirm={confirmDelete}
            confirmDisabled={isMutating}
            tone='danger'
            confirmLabel={
              <>
                {isMutating && <Loader2 className='h-4 w-4 animate-spin' />}
                Delete
              </>
            }
          />
        }
      >
        <p className='text-[13px] leading-5 text-[#334155]'>
          {deleteIds?.length > 1
            ? 'The selected mappings will be removed.'
            : `Mapping #${deleteIds?.[0]} ("${rows.find((m) => m.id === deleteIds?.[0])?.matchKey ?? ''}") will be removed.`}{' '}
          This cannot be undone.
        </p>
      </FormModal>
    </div>
  )
}
